import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Save, Plus, Pencil, Trash2, Percent, UtensilsCrossed, Users, DollarSign, X, Upload, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const CURRENCIES = [
  { code: "USD", symbol: "$" }, { code: "EUR", symbol: "\u20AC" },
  { code: "GBP", symbol: "\u00A3" }, { code: "AUD", symbol: "A$" },
  { code: "CAD", symbol: "C$" }, { code: "NZD", symbol: "NZ$" },
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function SortableBanner({ banner, idx, bannerUploading, onUpdateField, onUpload, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `banner-${idx}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : "auto" };
  const activeDays = banner.active_days || [];
  const allDays = activeDays.length === 0;

  const toggleDay = (dayIdx) => {
    let days = [...activeDays];
    if (days.includes(dayIdx)) {
      days = days.filter((d) => d !== dayIdx);
    } else {
      days.push(dayIdx);
      days.sort();
    }
    onUpdateField(idx, "active_days", days);
  };

  const toggleAllDays = () => {
    onUpdateField(idx, "active_days", allDays ? [new Date().getDay()] : []);
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className={`flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3 ${isDragging ? "bg-blue-50 shadow-md ring-1 ring-blue-300" : ""}`} data-testid={`weborder-banner-${idx}`}>
      <button {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 text-slate-300 hover:text-slate-500 touch-none flex-shrink-0 mt-4" data-testid={`drag-banner-${idx}`}>
        <GripVertical className="h-4 w-4" />
      </button>
      {banner.url && <img src={banner.url} alt="" className="w-24 h-16 rounded object-cover flex-shrink-0 border border-slate-200" />}
      <div className="flex-1 min-w-0 space-y-1.5">
        <Input value={banner.title || ""} onChange={(e) => onUpdateField(idx, "title", e.target.value)} placeholder="Banner title" className="h-7 text-xs" />
        <Input value={banner.subtitle || ""} onChange={(e) => onUpdateField(idx, "subtitle", e.target.value)} placeholder="Subtitle" className="h-7 text-xs" />
        <div className="flex items-center gap-1.5">
          <Input value={banner.url || ""} onChange={(e) => onUpdateField(idx, "url", e.target.value)} placeholder="Image URL" className="h-7 text-xs font-mono flex-1" />
          <label className={`inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-100 cursor-pointer flex-shrink-0 ${bannerUploading ? "opacity-50 pointer-events-none" : ""}`} data-testid={`upload-banner-${idx}`}>
            <Upload className="h-3 w-3" />
            <span>{bannerUploading ? "..." : "Upload"}</span>
            <input type="file" accept="image/*" onChange={(e) => onUpload(e, idx)} className="hidden" />
          </label>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold text-slate-500 uppercase">Show on:</span>
          <button
            onClick={toggleAllDays}
            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border transition-colors ${allDays ? "bg-blue-100 border-blue-300 text-blue-700" : "bg-slate-100 border-slate-200 text-slate-400 hover:border-slate-300"}`}
            data-testid={`banner-${idx}-all-days`}
          >
            Every day
          </button>
          {DAYS.map((day, dayIdx) => (
            <button
              key={dayIdx}
              onClick={() => toggleDay(dayIdx)}
              disabled={allDays}
              className={`w-7 h-5 rounded text-[10px] font-semibold border transition-colors ${
                allDays ? "bg-blue-50 border-blue-200 text-blue-400 cursor-default" :
                activeDays.includes(dayIdx) ? "bg-blue-100 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
              }`}
              data-testid={`banner-${idx}-day-${dayIdx}`}
            >
              {day.charAt(0)}
            </button>
          ))}
        </div>
      </div>
      <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50 flex-shrink-0 mt-3" onClick={() => onRemove(idx)} data-testid={`remove-banner-${idx}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("branding");

  // Tax states
  const [taxRates, setTaxRates] = useState([]);
  const [showTaxDialog, setShowTaxDialog] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [taxForm, setTaxForm] = useState({ name: "", rate: "", tax_type: "percentage", is_active: true, is_default: false, sort_order: 0 });

  // Dining type states
  const [diningTypes, setDiningTypes] = useState([]);
  const [showDiningDialog, setShowDiningDialog] = useState(false);
  const [editingDining, setEditingDining] = useState(null);
  const [diningForm, setDiningForm] = useState({ name: "", short_name: "", sort_order: 0, is_active: true, is_default: true, minutes_before_close: 0, minutes_before_open: 0 });

  // Customer group states
  const [customerGroups, setCustomerGroups] = useState([]);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: "", discount_percent: "", discount_type: "percentage", color: "#2563EB", is_active: true });

  // Tip pool states
  const [tipPool, setTipPool] = useState(null);
  const [tipSaving, setTipSaving] = useState(false);
  const [posRoles, setPosRoles] = useState([]);

  // Weborder states
  const [allProducts, setAllProducts] = useState([]);
  const [newBannerUrl, setNewBannerUrl] = useState("");
  const [newBannerTitle, setNewBannerTitle] = useState("");
  const [newBannerSubtitle, setNewBannerSubtitle] = useState("");
  const [bannerUploading, setBannerUploading] = useState(false);

  // Drag-and-drop sensors for banner reordering
  const bannerSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleBannerDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = parseInt(active.id.replace("banner-", ""));
    const newIdx = parseInt(over.id.replace("banner-", ""));
    const banners = [...(settings?.weborder_banner_images || [])];
    const reordered = arrayMove(banners, oldIdx, newIdx);
    setSettings((prev) => ({ ...prev, weborder_banner_images: reordered }));
  }, [settings]);

  const updateBannerField = useCallback((idx, field, value) => {
    setSettings((prev) => {
      const banners = [...(prev?.weborder_banner_images || [])];
      banners[idx] = { ...banners[idx], [field]: value };
      return { ...prev, weborder_banner_images: banners };
    });
  }, []);

  const handleBannerUpload = async (e, targetIdx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const uploadedUrl = res.data.url;
      if (targetIdx !== undefined && targetIdx !== null) {
        // Replace existing banner image
        const banners = [...(settings?.weborder_banner_images || [])];
        banners[targetIdx] = { ...banners[targetIdx], url: uploadedUrl };
        const updatedSettings = { ...settings, weborder_banner_images: banners };
        setSettings(updatedSettings);
        await api.put("/settings", updatedSettings);
        toast.success("Banner image updated & saved!");
      } else {
        // Auto-add new banner and save
        const title = newBannerTitle || "";
        const subtitle = newBannerSubtitle || "";
        const banners = [...(settings?.weborder_banner_images || []), { url: uploadedUrl, title, subtitle }];
        const updatedSettings = { ...settings, weborder_banner_images: banners };
        setSettings(updatedSettings);
        setNewBannerUrl(""); setNewBannerTitle(""); setNewBannerSubtitle("");
        await api.put("/settings", updatedSettings);
        toast.success("Banner uploaded & saved!");
      }
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || "Unknown error"));
    } finally { setBannerUploading(false); e.target.value = ""; }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sRes, tRes, dRes, gRes, tpRes, prRes, prodRes] = await Promise.all([api.get("/settings"), api.get("/tax-rates"), api.get("/dining-types"), api.get("/customer-groups"), api.get("/tip-pool-settings"), api.get("/pos-roles"), api.get("/products?active_only=true")]);
        setSettings(sRes.data);
        setTaxRates(tRes.data);
        setDiningTypes(dRes.data);
        setCustomerGroups(gRes.data);
        setTipPool(tpRes.data);
        setPosRoles(prRes.data);
        setAllProducts(prodRes.data);
      } catch (err) {
        console.error("Failed to load settings:", err);
      } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings", settings);
      setSettings(res.data);
      if (settings.brand_primary_color) document.documentElement.style.setProperty("--brand-primary", settings.brand_primary_color);
      if (settings.brand_accent_color) document.documentElement.style.setProperty("--brand-accent", settings.brand_accent_color);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save settings"); }
    finally { setSaving(false); }
  };

  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  // Tax CRUD
  const fetchTaxRates = async () => {
    try { const res = await api.get("/tax-rates"); setTaxRates(res.data); }
    catch { toast.error("Failed to load tax rates"); }
  };

  const openAddTax = () => {
    setEditingTax(null);
    setTaxForm({ name: "", rate: "", tax_type: "percentage", is_active: true, is_default: false, sort_order: taxRates.length + 1 });
    setShowTaxDialog(true);
  };

  const openEditTax = (tax) => {
    setEditingTax(tax);
    setTaxForm({ name: tax.name, rate: String(tax.rate), tax_type: tax.tax_type || "percentage", is_active: tax.is_active !== false, is_default: tax.is_default || false, sort_order: tax.sort_order || 0 });
    setShowTaxDialog(true);
  };

  const saveTax = async () => {
    if (!taxForm.name.trim()) { toast.error("Tax name is required"); return; }
    try {
      const payload = { ...taxForm, rate: parseFloat(taxForm.rate) || 0, sort_order: parseInt(taxForm.sort_order) || 0 };
      if (editingTax) { await api.put(`/tax-rates/${editingTax.tax_id}`, payload); toast.success("Tax rate updated"); }
      else { await api.post("/tax-rates", payload); toast.success("Tax rate created"); }
      setShowTaxDialog(false);
      fetchTaxRates();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };

  const deleteTax = async (id) => {
    if (!window.confirm("Delete this tax rate? It will be removed from all products.")) return;
    try { await api.delete(`/tax-rates/${id}`); toast.success("Deleted"); fetchTaxRates(); }
    catch { toast.error("Failed to delete"); }
  };

  const totalDefaultRate = taxRates.filter((t) => t.is_default && t.is_active).reduce((sum, t) => sum + t.rate, 0);

  // Dining type CRUD
  const fetchDiningTypes = async () => {
    try { const res = await api.get("/dining-types"); setDiningTypes(res.data); }
    catch { toast.error("Failed to load dining types"); }
  };
  const openAddDining = () => {
    setEditingDining(null);
    setDiningForm({ name: "", short_name: "", sort_order: diningTypes.length + 1, is_active: true, is_default: true });
    setShowDiningDialog(true);
  };
  const openEditDining = (dt) => {
    setEditingDining(dt);
    setDiningForm({ name: dt.name, short_name: dt.short_name || "", sort_order: dt.sort_order || 0, is_active: dt.is_active !== false, is_default: dt.is_default !== false, minutes_before_close: dt.minutes_before_close || 0, minutes_before_open: dt.minutes_before_open || 0 });
    setShowDiningDialog(true);
  };
  const saveDining = async () => {
    if (!diningForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = { ...diningForm, sort_order: parseInt(diningForm.sort_order) || 0 };
      if (editingDining) { await api.put(`/dining-types/${editingDining.dining_type_id}`, payload); toast.success("Updated"); }
      else { await api.post("/dining-types", payload); toast.success("Created"); }
      setShowDiningDialog(false); fetchDiningTypes();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteDining = async (id) => {
    if (!window.confirm("Delete this dining type? It will be removed from all products.")) return;
    try { await api.delete(`/dining-types/${id}`); toast.success("Deleted"); fetchDiningTypes(); }
    catch { toast.error("Failed"); }
  };

  // Customer Group CRUD
  const fetchCustomerGroups = async () => {
    try { const res = await api.get("/customer-groups"); setCustomerGroups(res.data); }
    catch { toast.error("Failed to load customer groups"); }
  };
  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: "", discount_percent: "", discount_type: "percentage", color: "#2563EB", is_active: true });
    setShowGroupDialog(true);
  };
  const openEditGroup = (g) => {
    setEditingGroup(g);
    setGroupForm({ name: g.name, discount_percent: String(g.discount_percent || 0), discount_type: g.discount_type || "percentage", color: g.color || "#2563EB", is_active: g.is_active !== false });
    setShowGroupDialog(true);
  };
  const saveGroup = async () => {
    if (!groupForm.name.trim()) { toast.error("Group name is required"); return; }
    try {
      const payload = { ...groupForm, discount_percent: parseFloat(groupForm.discount_percent) || 0 };
      if (editingGroup) { await api.put(`/customer-groups/${editingGroup.customer_group_id}`, payload); toast.success("Group updated"); }
      else { await api.post("/customer-groups", payload); toast.success("Group created"); }
      setShowGroupDialog(false);
      fetchCustomerGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
  };
  const deleteGroup = async (id) => {
    if (!window.confirm("Delete this customer group? Customers in this group will be unassigned.")) return;
    try { await api.delete(`/customer-groups/${id}`); toast.success("Deleted"); fetchCustomerGroups(); }
    catch { toast.error("Failed"); }
  };

  // Tip Pool CRUD
  const updateTip = (key, val) => setTipPool((p) => ({ ...p, [key]: val }));
  const saveTipPool = async () => {
    setTipSaving(true);
    try {
      const res = await api.put("/tip-pool-settings", tipPool);
      setTipPool(res.data);
      toast.success("Tip pool settings saved");
    } catch { toast.error("Failed to save tip pool settings"); }
    finally { setTipSaving(false); }
  };
  const addTipRole = () => setTipPool((p) => ({ ...p, tip_roles: [...(p.tip_roles || []), { pos_role_id: "", tip_in_pct: 100, tip_share: true }] }));
  const removeTipRole = (idx) => setTipPool((p) => ({ ...p, tip_roles: p.tip_roles.filter((_, i) => i !== idx) }));
  const updateTipRole = (idx, key, val) => setTipPool((p) => { const tr = [...(p.tip_roles || [])]; tr[idx] = { ...tr[idx], [key]: val }; return { ...p, tip_roles: tr }; });

  if (loading || !settings) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="settings-title">Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Customize your POS branding, taxes, and configuration</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6" data-testid="settings-tabs">
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="business" data-testid="tab-business">Business</TabsTrigger>
          <TabsTrigger value="taxes" data-testid="tab-taxes">
            Tax Settings
            <Badge variant="secondary" className="ml-2 text-xs">{taxRates.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="dining" data-testid="tab-dining">
            Dining Types
            <Badge variant="secondary" className="ml-2 text-xs">{diningTypes.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">
            Customer Groups
            <Badge variant="secondary" className="ml-2 text-xs">{customerGroups.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="tippool" data-testid="tab-tippool">
            Tip Pooling
          </TabsTrigger>
          <TabsTrigger value="store" data-testid="tab-store">
            Store / Receipt
          </TabsTrigger>
          <TabsTrigger value="weborder" data-testid="tab-weborder">
            Web Order
          </TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="tab-loyalty">
            Loyalty
          </TabsTrigger>
        </TabsList>

        {/* === BRANDING TAB === */}
        <TabsContent value="branding">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="branding-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Branding</h2>
              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="settings-save-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Business Name</Label>
              <Input value={settings.brand_name || ""} onChange={(e) => update("brand_name", e.target.value)} data-testid="settings-brand-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Logo URL</Label>
              <Input value={settings.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} placeholder="https://example.com/logo.png" data-testid="settings-logo-url" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Primary Color</Label>
                <div className="flex gap-2 items-center mt-1">
                  <input type="color" value={settings.brand_primary_color || "#2563EB"} onChange={(e) => update("brand_primary_color", e.target.value)} className="h-9 w-12 rounded border cursor-pointer" data-testid="settings-primary-color" />
                  <Input value={settings.brand_primary_color || ""} onChange={(e) => update("brand_primary_color", e.target.value)} className="flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Accent Color</Label>
                <div className="flex gap-2 items-center mt-1">
                  <input type="color" value={settings.brand_accent_color || "#0EA5E9"} onChange={(e) => update("brand_accent_color", e.target.value)} className="h-9 w-12 rounded border cursor-pointer" data-testid="settings-accent-color" />
                  <Input value={settings.brand_accent_color || ""} onChange={(e) => update("brand_accent_color", e.target.value)} className="flex-1" />
                </div>
              </div>
            </div>
            {/* Preview */}
            <div className="p-4 rounded-lg border border-slate-100 bg-slate-50 mt-4">
              <div className="flex items-center gap-3 mb-3">
                {settings.logo_url && <img src={settings.logo_url} alt="Logo" className="h-8 w-8 rounded" />}
                <span className="font-heading text-xl font-bold" style={{ color: settings.brand_primary_color }}>{settings.brand_name || "Your Brand"}</span>
              </div>
              <div className="flex gap-3">
                <button className="px-4 py-2 rounded-lg text-sm text-white font-medium" style={{ backgroundColor: settings.brand_primary_color }}>Primary</button>
                <button className="px-4 py-2 rounded-lg text-sm text-white font-medium" style={{ backgroundColor: settings.brand_accent_color }}>Accent</button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* === BUSINESS TAB === */}
        <TabsContent value="business">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="business-section">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Business Configuration</h2>
              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="business-save-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Business Type</Label>
                <Select value={settings.business_type || "both"} onValueChange={(v) => update("business_type", v)}>
                  <SelectTrigger data-testid="settings-business-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Default Tax Rate (%) <span className="text-slate-400">- legacy</span></Label>
                <Input type="number" step="0.1" value={settings.tax_rate || ""} onChange={(e) => update("tax_rate", parseFloat(e.target.value) || 0)} data-testid="settings-tax-rate" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Currency</Label>
                <Select value={settings.currency || "USD"} onValueChange={(v) => {
                  const cur = CURRENCIES.find((c) => c.code === v);
                  update("currency", v);
                  if (cur) update("currency_symbol", cur.symbol);
                }}>
                  <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} ({c.symbol})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Currency Symbol</Label>
                <Input value={settings.currency_symbol || ""} onChange={(e) => update("currency_symbol", e.target.value)} data-testid="settings-currency-symbol" />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* === TAX SETTINGS TAB === */}
        <TabsContent value="taxes">
          <div className="space-y-4" data-testid="tax-settings-section">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-slate-900">Tax Rates</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Configure multiple tax types. Default taxes auto-apply to new products.
                    Combined default rate: <strong>{totalDefaultRate.toFixed(2)}%</strong>
                  </p>
                </div>
                <Button onClick={openAddTax} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-tax-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Tax Rate
                </Button>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="tax-rates-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tax Name</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-center">Type</TableHead>
                      <TableHead className="text-center">Default</TableHead>
                      <TableHead className="text-center">Active</TableHead>
                      <TableHead className="text-center">Order</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxRates.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No tax rates configured. Add your first tax rate.</TableCell></TableRow>
                    ) : taxRates.map((tax) => (
                      <TableRow key={tax.tax_id} data-testid={`tax-row-${tax.tax_id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Percent className="h-4 w-4 text-[var(--brand-primary)]" />
                            {tax.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">{tax.rate.toFixed(2)}%</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="text-xs capitalize">{tax.tax_type}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {tax.is_default ? (
                            <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">Default</Badge>
                          ) : <span className="text-xs text-slate-300">-</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={tax.is_active ? "default" : "secondary"} className="text-xs">{tax.is_active ? "Active" : "Inactive"}</Badge>
                        </TableCell>
                        <TableCell className="text-center text-sm text-slate-500">{tax.sort_order}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => openEditTax(tax)} data-testid={`edit-tax-${tax.tax_id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteTax(tax.tax_id)} data-testid={`del-tax-${tax.tax_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Tax Summary */}
              {taxRates.filter((t) => t.is_active).length > 0 && (
                <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="tax-summary">
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Tax Breakdown Example (on $10.00 item)</h3>
                  <div className="space-y-1">
                    {taxRates.filter((t) => t.is_active && t.is_default).map((tax) => (
                      <div key={tax.tax_id} className="flex justify-between text-sm">
                        <span className="text-slate-500">{tax.name} ({tax.rate}%)</span>
                        <span className="font-medium text-slate-700">${(10 * tax.rate / 100).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-1 mt-1">
                      <span className="text-slate-700">Total Tax</span>
                      <span className="text-slate-900">${(10 * totalDefaultRate / 100).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* === DINING TYPES TAB === */}
        <TabsContent value="dining">
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="dining-types-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-900">Dining Types</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Define how customers can order. Products can be restricted to specific dining types (e.g., alcohol → Dine In only).
                </p>
              </div>
              <Button onClick={openAddDining} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-dining-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Dining Type
              </Button>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="dining-types-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dining Type</TableHead>
                    <TableHead>Short Name</TableHead>
                    <TableHead className="text-center">Default</TableHead>
                    <TableHead className="text-center">Active</TableHead>
                    <TableHead className="text-center">Order</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {diningTypes.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No dining types. Add your first (e.g., Dine In, Pickup, Delivery).</TableCell></TableRow>
                  ) : diningTypes.map((dt) => (
                    <TableRow key={dt.dining_type_id} data-testid={`dining-row-${dt.dining_type_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <UtensilsCrossed className="h-4 w-4 text-[var(--brand-primary)]" />
                          <span className="font-medium">{dt.name}</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs font-mono">{dt.short_name || "-"}</Badge></TableCell>
                      <TableCell className="text-center">
                        {dt.is_default ? <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">Default</Badge> : <span className="text-xs text-slate-300">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={dt.is_active ? "default" : "secondary"} className="text-xs">{dt.is_active ? "Active" : "Inactive"}</Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm text-slate-500">{dt.sort_order}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditDining(dt)} data-testid={`edit-dining-${dt.dining_type_id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteDining(dt.dining_type_id)} data-testid={`del-dining-${dt.dining_type_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-1">How Dining Types Work</h3>
              <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                <li><strong>Default</strong> dining types are auto-assigned to new products (available for all order types)</li>
                <li>On each product, you can restrict which dining types are available</li>
                <li>Example: Alcohol items → only "Dine In" and "Bar", not "Delivery" or "Pickup"</li>
                <li>In the POS, products filter based on the selected dining type for the order</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* === CUSTOMER GROUPS TAB === */}
        <TabsContent value="groups">
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="customer-groups-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-900">Customer Groups</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Manage customer tiers with automatic discount percentages applied at checkout.
                </p>
              </div>
              <Button onClick={openAddGroup} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-group-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Group
              </Button>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="customer-groups-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Group Name</TableHead>
                    <TableHead className="text-right">Discount</TableHead>
                    <TableHead className="text-center">Type</TableHead>
                    <TableHead className="text-center">Color</TableHead>
                    <TableHead className="text-center">Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerGroups.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No customer groups. Create your first (e.g., VIP, Regular, Wholesale).</TableCell></TableRow>
                  ) : customerGroups.map((g) => (
                    <TableRow key={g.customer_group_id} data-testid={`group-row-${g.customer_group_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: g.color || "#2563EB" }} />
                          <span className="font-medium">{g.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">{(g.discount_percent || 0).toFixed(1)}%</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-xs capitalize">{g.discount_type || "percentage"}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="h-6 w-6 rounded-full mx-auto border border-slate-200" style={{ backgroundColor: g.color || "#2563EB" }} />
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={g.is_active ? "default" : "secondary"} className="text-xs">{g.is_active ? "Active" : "Inactive"}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditGroup(g)} data-testid={`edit-group-${g.customer_group_id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteGroup(g.customer_group_id)} data-testid={`del-group-${g.customer_group_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-1">How Customer Groups Work</h3>
              <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                <li>Assign customers to a group from the Customer detail page</li>
                <li>When a grouped customer is attached to a POS order, the group discount auto-applies</li>
                <li>Only active groups provide discounts at checkout</li>
                <li>Example: VIP group = 10% off all orders, Wholesale = 15% off</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* === TIP POOLING TAB === */}
        <TabsContent value="tippool">
          {tipPool && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="tip-pool-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-slate-900">Tip Pooling</h2>
                  <p className="text-sm text-slate-500 mt-1">Configure how tips are pooled and shared among employees</p>
                </div>
                <Button onClick={saveTipPool} disabled={tipSaving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-tippool-btn">
                  <Save className="h-4 w-4 mr-1" /> {tipSaving ? "Saving..." : "Save"}
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Allow Tip Pooling</Label>
                  <Switch checked={tipPool.allow_tip_pooling || false} onCheckedChange={(v) => updateTip("allow_tip_pooling", v)} data-testid="tp-allow" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Tip In Based On</Label>
                  <Select value={tipPool.tip_in_based_on || "tips"} onValueChange={(v) => updateTip("tip_in_based_on", v)}>
                    <SelectTrigger className="w-40" data-testid="tp-based-on"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tips">Tips</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Tip Share</Label>
                  <Select value={tipPool.tip_share_method || "even"} onValueChange={(v) => updateTip("tip_share_method", v)}>
                    <SelectTrigger className="w-40" data-testid="tp-share-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="even">Even</SelectItem>
                      <SelectItem value="weighted">Weighted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Treat Declared Tips As</Label>
                  <Select value={tipPool.treat_declared_tips_as || "ignore"} onValueChange={(v) => updateTip("treat_declared_tips_as", v)}>
                    <SelectTrigger className="w-48" data-testid="tp-declared"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ignore">Ignore Declared Tips</SelectItem>
                      <SelectItem value="include">Include Declared Tips</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Include Tip in Amount in Cash Due Employee/House Lines</Label>
                  <Switch checked={tipPool.include_tip_in_cash_due || false} onCheckedChange={(v) => updateTip("include_tip_in_cash_due", v)} data-testid="tp-cash-due" />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-100">
                  <Label className="text-sm text-slate-700">Periodicity</Label>
                  <Select value={tipPool.periodicity || "daily"} onValueChange={(v) => updateTip("periodicity", v)}>
                    <SelectTrigger className="w-40" data-testid="tp-periodicity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tip In / Tip Share By Role */}
                <div className="pt-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Tip In / Tip Share By Role</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Role</TableHead>
                        <TableHead className="text-xs text-right">Tip In %</TableHead>
                        <TableHead className="text-xs text-center">Tip Share</TableHead>
                        <TableHead className="text-xs text-right w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(tipPool.tip_roles || []).map((tr, idx) => (
                        <TableRow key={idx} data-testid={`tp-role-${idx}`}>
                          <TableCell>
                            <Select value={tr.pos_role_id || "none"} onValueChange={(v) => updateTipRole(idx, "pos_role_id", v === "none" ? "" : v)}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select Role" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select Role</SelectItem>
                                {posRoles.filter((r) => r.is_active).map((r) => <SelectItem key={r.pos_role_id} value={r.pos_role_id}>{r.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <Input type="number" value={tr.tip_in_pct} onChange={(e) => updateTipRole(idx, "tip_in_pct", parseFloat(e.target.value) || 0)} className="h-8 w-20 text-xs text-right" />
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch checked={tr.tip_share !== false} onCheckedChange={(v) => updateTipRole(idx, "tip_share", v)} />
                          </TableCell>
                          <TableCell>
                            <button onClick={() => removeTipRole(idx)} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <button onClick={addTipRole} className="mt-2 text-sm text-[var(--brand-primary)] hover:underline font-medium" data-testid="tp-add-role">+ Add a Role</button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* === STORE / RECEIPT TAB === */}
        <TabsContent value="store">
          <div className="space-y-6">
            {/* Receipt Customization */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="receipt-section">
              <h3 className="text-base font-semibold text-slate-900">Receipt Customization</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Business Address</Label>
                  <Input value={settings?.business_address || ""} onChange={(e) => setSettings({ ...settings, business_address: e.target.value })} placeholder="123 Main St, City, State 12345" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Business Phone</Label>
                  <Input value={settings?.business_phone || ""} onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })} placeholder="(555) 123-4567" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Receipt Header (printed above items)</Label>
                <Input value={settings?.receipt_header || ""} onChange={(e) => setSettings({ ...settings, receipt_header: e.target.value })} placeholder="Welcome! Order online at..." />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Receipt Footer (printed at bottom)</Label>
                <textarea
                  value={settings?.receipt_footer || ""}
                  onChange={(e) => setSettings({ ...settings, receipt_footer: e.target.value })}
                  className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  placeholder="Thank you for your business!&#10;Follow us @cashuppos"
                />
              </div>

              {/* Per-template customer info toggles */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Show Customer Info On</p>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-4 gap-2 text-xs text-center mb-2">
                    <div />
                    <div className="font-semibold text-slate-500">Customer Receipt</div>
                    <div className="font-semibold text-slate-500">Kitchen Ticket</div>
                    <div className="font-semibold text-slate-500">Delivery Ticket</div>
                  </div>
                  {[
                    { label: "Customer Name", field: "show_customer_name" },
                    { label: "Address", field: "show_customer_address" },
                    { label: "Phone", field: "show_customer_phone" },
                  ].map(({ label, field }) => (
                    <div key={field} className="grid grid-cols-4 gap-2 items-center py-1.5">
                      <span className="text-sm text-slate-700">{label}</span>
                      {["receipt", "kitchen", "delivery"].map((prefix) => {
                        const key = `${prefix}_${field}`;
                        return (
                          <div key={key} className="text-center">
                            <Switch
                              checked={settings?.[key] !== false}
                              onCheckedChange={(v) => setSettings({ ...settings, [key]: v })}
                              data-testid={`toggle-${key}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-receipt-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Receipt Settings"}
              </Button>
            </div>

            {/* Hours of Operation */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="hours-section">
              <h3 className="text-base font-semibold text-slate-900">Hours of Operation</h3>
              <p className="text-sm text-slate-500">Set your store's operating hours. Leave open/close blank for 24hr or closed days.</p>
              <div className="space-y-2">
                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((dayName, idx) => {
                  const hours = (settings?.hours_of_operation || []);
                  const dayData = hours.find((h) => h.day === idx) || { day: idx, open: "", close: "", closed: false };
                  const updateDay = (field, value) => {
                    const existing = [...(settings?.hours_of_operation || [])];
                    const eIdx = existing.findIndex((h) => h.day === idx);
                    const updated = { ...dayData, [field]: value };
                    if (eIdx >= 0) existing[eIdx] = updated;
                    else existing.push(updated);
                    setSettings({ ...settings, hours_of_operation: existing });
                  };
                  return (
                    <div key={idx} className={`flex items-center gap-3 p-2 rounded-md ${dayData.closed ? "bg-red-50/50" : "bg-slate-50"}`} data-testid={`hours-day-${idx}`}>
                      <span className="text-sm font-medium text-slate-700 w-24">{dayName}</span>
                      <Switch checked={!dayData.closed} onCheckedChange={(v) => updateDay("closed", !v)} />
                      <span className="text-xs text-slate-500 w-12">{dayData.closed ? "Closed" : "Open"}</span>
                      {!dayData.closed && (
                        <>
                          <Input type="time" value={dayData.open || ""} onChange={(e) => updateDay("open", e.target.value)} className="h-8 text-xs w-28" />
                          <span className="text-xs text-slate-400">to</span>
                          <Input type="time" value={dayData.close || ""} onChange={(e) => updateDay("close", e.target.value)} className="h-8 text-xs w-28" />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-hours-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Hours"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* === WEB ORDER TAB === */}
        <TabsContent value="weborder">
          <div className="space-y-6">
            {/* General Settings */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="weborder-settings-section">
              <h3 className="text-base font-semibold text-slate-900">Web Order Settings</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                  <Switch checked={settings?.weborder_enabled || false} onCheckedChange={(v) => update("weborder_enabled", v)} data-testid="weborder-enabled-toggle" />
                  <Label className="text-sm text-slate-700 font-medium">Online Ordering Enabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={settings?.weborder_require_payment || false} onCheckedChange={(v) => update("weborder_require_payment", v)} data-testid="weborder-require-payment" />
                  <Label className="text-sm text-slate-700 font-medium">Require Payment at Order</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={settings?.weborder_pickup_enabled !== false} onCheckedChange={(v) => update("weborder_pickup_enabled", v)} data-testid="weborder-pickup-toggle" />
                  <Label className="text-sm text-slate-700">Pickup Orders</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={settings?.weborder_delivery_enabled !== false} onCheckedChange={(v) => update("weborder_delivery_enabled", v)} data-testid="weborder-delivery-toggle" />
                  <Label className="text-sm text-slate-700">Delivery Orders</Label>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Web Order URL Slug</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">cashuppos.com/</span>
                  <Input value={settings?.weborder_slug || ""} onChange={(e) => update("weborder_slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="yourstore" className="max-w-[200px] font-mono" data-testid="weborder-slug-input" />
                  <span className="text-sm text-slate-400">/weborder</span>
                </div>
              </div>
              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-weborder-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Settings"}
              </Button>
            </div>

            {/* Banner Images */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="weborder-banners-section">
              <h3 className="text-base font-semibold text-slate-900">Banner Images (Specials/Promos)</h3>
              <p className="text-sm text-slate-500">These banners rotate at the top of the web ordering page. Drag to reorder — the first banner appears first.</p>

              {/* Existing banners - draggable */}
              <DndContext sensors={bannerSensors} collisionDetection={closestCenter} onDragEnd={handleBannerDragEnd}>
                <SortableContext items={(settings?.weborder_banner_images || []).map((_, i) => `banner-${i}`)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {(settings?.weborder_banner_images || []).map((banner, idx) => (
                      <SortableBanner
                        key={`banner-${idx}`}
                        banner={banner}
                        idx={idx}
                        bannerUploading={bannerUploading}
                        onUpdateField={updateBannerField}
                        onUpload={handleBannerUpload}
                        onRemove={(i) => {
                          const banners = (settings?.weborder_banner_images || []).filter((_, j) => j !== i);
                          update("weborder_banner_images", banners);
                        }}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              {/* Add new banner */}
              <div className="border border-dashed border-slate-300 rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Add New Banner</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="flex items-center gap-1.5">
                    <Input value={newBannerUrl} onChange={(e) => setNewBannerUrl(e.target.value)} placeholder="Image URL" className="text-xs font-mono flex-1" data-testid="new-banner-url" />
                    <label className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-100 cursor-pointer flex-shrink-0 ${bannerUploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-new-banner">
                      <Upload className="h-3.5 w-3.5" />
                      <span>{bannerUploading ? "Uploading..." : "Upload"}</span>
                      <input type="file" accept="image/*" onChange={(e) => handleBannerUpload(e, null)} className="hidden" />
                    </label>
                  </div>
                  <Input value={newBannerTitle} onChange={(e) => setNewBannerTitle(e.target.value)} placeholder="Title" className="text-xs" data-testid="new-banner-title" />
                  <Input value={newBannerSubtitle} onChange={(e) => setNewBannerSubtitle(e.target.value)} placeholder="Subtitle" className="text-xs" />
                </div>
                <Button variant="outline" size="sm" onClick={() => {
                  if (!newBannerUrl) { toast.error("Image URL or upload required"); return; }
                  const banners = [...(settings?.weborder_banner_images || []), { url: newBannerUrl, title: newBannerTitle, subtitle: newBannerSubtitle }];
                  update("weborder_banner_images", banners);
                  setNewBannerUrl(""); setNewBannerTitle(""); setNewBannerSubtitle("");
                }} data-testid="add-banner-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Banner
                </Button>
              </div>

              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-banners-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Banners"}
              </Button>
            </div>

            {/* Featured Products */}
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="weborder-featured-section">
              <h3 className="text-base font-semibold text-slate-900">Featured Products</h3>
              <p className="text-sm text-slate-500">Select products to feature at the top of the web order page. Leave empty to auto-select products with images.</p>

              {/* Currently featured */}
              {(settings?.weborder_featured_product_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {(settings?.weborder_featured_product_ids || []).map((pid) => {
                    const prod = allProducts.find((p) => p.product_id === pid);
                    return prod ? (
                      <Badge key={pid} variant="outline" className="text-xs text-blue-700 border-blue-300 bg-blue-50 gap-1">
                        {prod.name}
                        <button onClick={() => update("weborder_featured_product_ids", (settings?.weborder_featured_product_ids || []).filter((id) => id !== pid))} className="hover:text-red-500">x</button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}

              {/* Product picker */}
              <div className="max-h-[200px] overflow-y-auto space-y-0.5 border border-slate-100 rounded-lg p-2">
                {allProducts.filter((p) => !(settings?.weborder_featured_product_ids || []).includes(p.product_id)).map((p) => (
                  <label key={p.product_id} className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-slate-50 text-xs">
                    <Checkbox checked={false} onCheckedChange={() => {
                      update("weborder_featured_product_ids", [...(settings?.weborder_featured_product_ids || []), p.product_id]);
                    }} className="h-3 w-3" />
                    {p.image_url && <img src={p.image_url} alt="" className="h-6 w-6 rounded object-cover" />}
                    <span className="text-slate-700 truncate flex-1">{p.name}</span>
                    <span className="text-slate-400">${(p.price || 0).toFixed(2)}</span>
                  </label>
                ))}
              </div>

              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-featured-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Featured Products"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* === LOYALTY TAB === */}
        <TabsContent value="loyalty">
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="loyalty-settings-section">
              <h3 className="text-base font-semibold text-slate-900">Loyalty Program</h3>
              <p className="text-sm text-slate-500">Configure points earning, redemption, and bonus events. Customers auto-enroll when they create an account.</p>

              <div className="flex items-center gap-2">
                <Switch checked={settings?.loyalty_enabled || false} onCheckedChange={(v) => update("loyalty_enabled", v)} data-testid="loyalty-enabled-toggle" />
                <Label className="text-sm text-slate-700 font-semibold">Enable Loyalty Program</Label>
              </div>

              {settings?.loyalty_enabled && (
                <div className="space-y-4 pt-2">
                  {/* Points Earning */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-800">Points Earning</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Points per $1 Spent</Label>
                        <Input type="number" min="0" step="0.1" value={settings?.loyalty_points_per_dollar ?? 1} onChange={(e) => update("loyalty_points_per_dollar", parseFloat(e.target.value) || 0)} className="mt-1" data-testid="loyalty-points-per-dollar" />
                        <p className="text-[10px] text-slate-400 mt-0.5">e.g., 1 = 1 point per $1, 2 = 2 points per $1</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">Bonus points can also be set per-product in the Product Detail page.</p>
                  </div>

                  {/* Redemption */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-800">Redemption</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Minimum Points to Redeem</Label>
                        <Input type="number" min="1" value={settings?.loyalty_min_points_redeem ?? 100} onChange={(e) => update("loyalty_min_points_redeem", parseInt(e.target.value) || 1)} className="mt-1" data-testid="loyalty-min-redeem" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Discount Amount ($) on Redemption</Label>
                        <Input type="number" min="0" step="0.01" value={settings?.loyalty_redeem_discount ?? 5} onChange={(e) => update("loyalty_redeem_discount", parseFloat(e.target.value) || 0)} className="mt-1" data-testid="loyalty-redeem-discount" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">When a customer redeems, they spend the minimum points and get ${settings?.loyalty_redeem_discount ?? 5} off their order.</p>
                  </div>

                  {/* Expiration */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-800">Points Expiration</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Expire After (months, 0 = never)</Label>
                        <Input type="number" min="0" value={settings?.loyalty_points_expiry_months ?? 0} onChange={(e) => update("loyalty_points_expiry_months", parseInt(e.target.value) || 0)} className="mt-1" data-testid="loyalty-expiry-months" />
                      </div>
                    </div>
                  </div>

                  {/* VIP Tiers */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-800">VIP Tiers</h4>
                    <p className="text-xs text-slate-500">Reward loyal customers with status tiers based on lifetime points.</p>
                    {(settings?.loyalty_vip_tiers || []).map((tier, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded p-2">
                        <Input value={tier.name || ""} onChange={(e) => { const t = [...(settings?.loyalty_vip_tiers || [])]; t[i] = { ...t[i], name: e.target.value }; update("loyalty_vip_tiers", t); }} placeholder="Tier name" className="h-7 text-xs flex-1" />
                        <Input type="number" value={tier.min_points ?? 0} onChange={(e) => { const t = [...(settings?.loyalty_vip_tiers || [])]; t[i] = { ...t[i], min_points: parseInt(e.target.value) || 0 }; update("loyalty_vip_tiers", t); }} placeholder="Min pts" className="h-7 text-xs w-24" />
                        <Input value={tier.perks || ""} onChange={(e) => { const t = [...(settings?.loyalty_vip_tiers || [])]; t[i] = { ...t[i], perks: e.target.value }; update("loyalty_vip_tiers", t); }} placeholder="Perks description" className="h-7 text-xs flex-1" />
                        <Button variant="outline" size="sm" className="text-red-500 h-7 px-2" onClick={() => { const t = (settings?.loyalty_vip_tiers || []).filter((_, j) => j !== i); update("loyalty_vip_tiers", t); }}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => update("loyalty_vip_tiers", [...(settings?.loyalty_vip_tiers || []), { name: "", min_points: 0, perks: "" }])} data-testid="add-vip-tier">
                      <Plus className="h-3 w-3 mr-1" /> Add Tier
                    </Button>
                  </div>

                  {/* Bonus Events */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-800">Bonus Point Events</h4>
                    <p className="text-xs text-slate-500">Multiply points earned on specific days (e.g., "Double Points Tuesdays").</p>
                    {(settings?.loyalty_bonus_events || []).map((evt, i) => (
                      <div key={i} className="bg-white border border-slate-200 rounded p-2 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Input value={evt.name || ""} onChange={(e) => { const evts = [...(settings?.loyalty_bonus_events || [])]; evts[i] = { ...evts[i], name: e.target.value }; update("loyalty_bonus_events", evts); }} placeholder="Event name" className="h-7 text-xs flex-1" />
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-slate-500">Multiplier:</span>
                            <Input type="number" min="1" step="0.5" value={evt.multiplier ?? 2} onChange={(e) => { const evts = [...(settings?.loyalty_bonus_events || [])]; evts[i] = { ...evts[i], multiplier: parseFloat(e.target.value) || 1 }; update("loyalty_bonus_events", evts); }} className="h-7 text-xs w-16" />
                            <span className="text-[10px] text-slate-500">x</span>
                          </div>
                          <Button variant="outline" size="sm" className="text-red-500 h-7 px-2" onClick={() => { const evts = (settings?.loyalty_bonus_events || []).filter((_, j) => j !== i); update("loyalty_bonus_events", evts); }}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase">Days:</span>
                          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, dayIdx) => (
                            <button key={dayIdx} onClick={() => {
                              const evts = [...(settings?.loyalty_bonus_events || [])];
                              let days = [...(evts[i].days || [])];
                              if (days.includes(dayIdx)) days = days.filter(d => d !== dayIdx);
                              else { days.push(dayIdx); days.sort(); }
                              evts[i] = { ...evts[i], days };
                              update("loyalty_bonus_events", evts);
                            }}
                            className={`w-7 h-5 rounded text-[10px] font-semibold border transition-colors ${(evt.days || []).includes(dayIdx) ? "bg-blue-100 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"}`}
                            >{day.charAt(0)}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => update("loyalty_bonus_events", [...(settings?.loyalty_bonus_events || []), { name: "", multiplier: 2, days: [] }])} data-testid="add-bonus-event">
                      <Plus className="h-3 w-3 mr-1" /> Add Event
                    </Button>
                  </div>

                  {/* Referral Bonus */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-800">Referral Bonus</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Points for Referral (0 = disabled)</Label>
                        <Input type="number" min="0" value={settings?.loyalty_referral_bonus ?? 0} onChange={(e) => update("loyalty_referral_bonus", parseInt(e.target.value) || 0)} className="mt-1" data-testid="loyalty-referral-bonus" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Button onClick={handleSaveSettings} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="save-loyalty-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save Loyalty Settings"}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Tax Dialog */}
      <Dialog open={showTaxDialog} onOpenChange={setShowTaxDialog}>
        <DialogContent data-testid="tax-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingTax ? "Edit Tax Rate" : "New Tax Rate"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Configure a tax that can be applied to products</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Tax Name</Label>
              <Input value={taxForm.name} onChange={(e) => setTaxForm({ ...taxForm, name: e.target.value })} placeholder="e.g., State Tax, Local Tax, County Tax" data-testid="tax-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Rate (%)</Label>
                <Input type="number" step="0.01" value={taxForm.rate} onChange={(e) => setTaxForm({ ...taxForm, rate: e.target.value })} placeholder="6.25" data-testid="tax-rate-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Type</Label>
                <Select value={taxForm.tax_type} onValueChange={(v) => setTaxForm({ ...taxForm, tax_type: v })}>
                  <SelectTrigger data-testid="tax-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={taxForm.is_default} onCheckedChange={(v) => setTaxForm({ ...taxForm, is_default: v })} data-testid="tax-default-switch" />
                <Label className="text-sm">Default (auto-apply to products)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={taxForm.is_active} onCheckedChange={(v) => setTaxForm({ ...taxForm, is_active: v })} />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Sort Order</Label>
              <Input type="number" value={taxForm.sort_order} onChange={(e) => setTaxForm({ ...taxForm, sort_order: e.target.value })} className="max-w-[100px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaxDialog(false)}>Cancel</Button>
            <Button onClick={saveTax} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="tax-save-btn">{editingTax ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dining Type Dialog */}
      <Dialog open={showDiningDialog} onOpenChange={setShowDiningDialog}>
        <DialogContent data-testid="dining-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingDining ? "Edit Dining Type" : "New Dining Type"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Define an order/dining type for your establishment</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input value={diningForm.name} onChange={(e) => setDiningForm({ ...diningForm, name: e.target.value })} placeholder="e.g., Dine In, Pickup, Delivery, Bar" data-testid="dining-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Short Name</Label>
                <Input value={diningForm.short_name} onChange={(e) => setDiningForm({ ...diningForm, short_name: e.target.value })} placeholder="DI" data-testid="dining-short-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort Order</Label>
                <Input type="number" value={diningForm.sort_order} onChange={(e) => setDiningForm({ ...diningForm, sort_order: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={diningForm.is_default} onCheckedChange={(v) => setDiningForm({ ...diningForm, is_default: v })} data-testid="dining-default-switch" />
                <Label className="text-sm">Default (auto-assign to new products)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={diningForm.is_active} onCheckedChange={(v) => setDiningForm({ ...diningForm, is_active: v })} />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Minutes before open (early orders)</Label>
                <Input type="number" min="0" value={diningForm.minutes_before_open} onChange={(e) => setDiningForm({ ...diningForm, minutes_before_open: parseInt(e.target.value) || 0 })} data-testid="dining-before-open" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Minutes before close (cutoff)</Label>
                <Input type="number" min="0" value={diningForm.minutes_before_close} onChange={(e) => setDiningForm({ ...diningForm, minutes_before_close: parseInt(e.target.value) || 0 })} data-testid="dining-before-close" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiningDialog(false)}>Cancel</Button>
            <Button onClick={saveDining} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="dining-save-btn">{editingDining ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingGroup ? "Edit Customer Group" : "New Customer Group"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Configure a customer tier with automatic discounts</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Group Name</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="e.g., VIP, Wholesale, Employee" data-testid="group-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Discount (%)</Label>
                <Input type="number" step="0.1" value={groupForm.discount_percent} onChange={(e) => setGroupForm({ ...groupForm, discount_percent: e.target.value })} placeholder="10" data-testid="group-discount-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Discount Type</Label>
                <Select value={groupForm.discount_type} onValueChange={(v) => setGroupForm({ ...groupForm, discount_type: v })}>
                  <SelectTrigger data-testid="group-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <Label className="text-xs text-slate-500">Color</Label>
                <div className="flex gap-2 items-center mt-1">
                  <input type="color" value={groupForm.color} onChange={(e) => setGroupForm({ ...groupForm, color: e.target.value })} className="h-9 w-12 rounded border cursor-pointer" data-testid="group-color-input" />
                  <Input value={groupForm.color} onChange={(e) => setGroupForm({ ...groupForm, color: e.target.value })} className="w-28" />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch checked={groupForm.is_active} onCheckedChange={(v) => setGroupForm({ ...groupForm, is_active: v })} data-testid="group-active-switch" />
                <Label className="text-sm">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
            <Button onClick={saveGroup} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="group-save-btn">{editingGroup ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
